<script setup lang="ts">
import { ref, watch } from "vue";
import {
  TuButton,
  TuInputLabel,
  TuInputText,
  FieldValidationRule,
} from "@tinyurldev/tu-ui-vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";

import useTranslation from "@Composables/useTranslation";

//
// Setup
const { __ } = useTranslation();

//
// Props
interface Props {
  name: string;
  loading?: boolean;
  errors?: Record<string, string>;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  errors: () => ({}),
});

//
// Emit
interface Events {
  (e: "submit", name: string): void;
  (e: "validation-error"): void;
}

const emit = defineEmits<Events>();

//
// Data

const name = ref(props.name);
const localError = ref<string | null>(null);
const validations: FieldValidationRule[] = [
  {
    validator: (value: string) => value.trim() !== "",
    errorMessage: __("organization_profile_settings.update_name_form.name_required_error"),
  },
  {
    validator: (value: string) => value.length <= 255,
    errorMessage: __("organization_profile_settings.update_name_form.name_too_long_error"),
  },
];

const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");

//
// Computed

//
// Methods

function cleanUpError(): void {
  localError.value = null;
}

function isInputValid(): boolean {
  return validations.every((rule) => {
    const isValid = rule.validator(name.value);

    if (!isValid) {
      emit("validation-error");

      localError.value = typeof rule.errorMessage === "string"
        ? rule.errorMessage
        : rule.errorMessage();
    }

    return isValid;
  });
}

function onSubmit(): void {
  if (props.loading || !isInputValid()) {
    return;
  }

  emit("submit", name.value);
}

//
// Watchers
watch(() => props.name, (givenName) => {
  name.value = givenName;
});

watch(() => props.errors, (givenError) => {
  localError.value = givenError.name ?? null;
});

//
// Exposed Test Vars
defineExpose({ localError });
</script>

<template>
  <form
    class="org-name-form"
    :class="{ 'org-name-form--mobile': isMobileView }"
    @submit.prevent="onSubmit"
  >
    <div class="org-name-form__input">
      <TuInputLabel
        id="name"
        class="org-name-form__label"
        :label="__('organization_profile_settings.update_name_form.name_label')"
      />

      <TuInputText
        id="name"
        v-model="name"
        class="_name"
        :copy="{ ...(localError && {errorMessage: localError}) }"
        @focus="cleanUpError"
        @focusout="cleanUpError"
      />
    </div>

    <div class="org-name-form__input">
      <div class="org-name-form__submission">
        <TuButton
          class="_submit"
          :label="__('organization_profile_settings.update_name_form.submit')"
          :is-disabled="loading"
          :is-block="isMobileView"
        />
      </div>
    </div>
  </form>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;

.org-name-form {
  $self: &;

  display: flex;
  flex-flow: column;
  gap: spacing.$xxxs;
  padding: spacing.$xxs;

  background-color: color.$background-secondary;
  border-radius: border.$medium-radius;
  border: border.$primary;

  &__input {
    display: grid;
    grid-template-areas: "side main";
    grid-template-columns: 1fr 3fr;
  }

  &__label {
    align-items: flex-start;
  }

  &__submission {
    grid-area: main;
  }

  &--mobile {
    #{ $self }__input {
      display: flex;
      flex-flow: column;
      gap: spacing.$xxxs;
    }
  }
}
</style>
